import { useEffect, useMemo, useState } from "react";
import { useChartSetSelectContext } from "components/charting/contexts/ChartSetSelectContext";
import type { MeasurementSet } from "types";
import ChartCompareSetSelectContext from "components/charting/contexts/ChartCompareSetSelectContext";
import ArrayHelpers from "helpers/ArrayHelpers";

interface ChartCompareSetSelectProviderProps {
  children?: React.ReactNode;
}

function ChartCompareSetSelectProvider(props: ChartCompareSetSelectProviderProps) {
  const { value: baselineSets, options: availableSets } = useChartSetSelectContext();
  const [selectedCompareSets, setSelectedCompareSets] = useState<MeasurementSet[]>([]);

  // A set picked as a baseline elsewhere (e.g. on the set comparison chart, which has no comparison selector)
  // leaves the comparison, so it is never both.
  const comparableSets = useMemo(() => {
    const baselineIds = new Set(baselineSets.map((set) => set.measurementId));
    return availableSets.filter((set) => !baselineIds.has(set.measurementId));
  }, [availableSets, baselineSets]);

  // Drop selected sets once they stop being offered, so they don't return when the exercise or athlete is switched back.
  useEffect(() => {
    setSelectedCompareSets((sets) => ArrayHelpers.reconcile(sets, comparableSets, (set) => set.measurementId));
  }, [comparableSets]);

  const contextValue = useMemo(
    () => ({
      value: ArrayHelpers.reconcile(selectedCompareSets, comparableSets, (set) => set.measurementId),
      options: availableSets,
      onChange: (sets: MeasurementSet[]) => {
        setSelectedCompareSets(sets);
      },
    }),
    [availableSets, comparableSets, selectedCompareSets]
  );

  return (
    <ChartCompareSetSelectContext.Provider value={contextValue}>{props.children}</ChartCompareSetSelectContext.Provider>
  );
}

export default ChartCompareSetSelectProvider;
